import React, { useLayoutEffect, useRef } from 'react';
import { Bold, Italic, CornerDownLeft } from 'lucide-react';

type Props = {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  monospace?: boolean;
  /** Shown under toolbar */
  hint?: string;
};

/**
 * Lightweight HTML fragment editor: toolbar inserts tags; values are sanitized on preview/PDF.
 */
export const InvoiceTemplateHtmlEditor: React.FC<Props> = ({
  value,
  onChange,
  placeholder,
  rows = 8,
  monospace,
  hint = 'Bold / italic / line break insert HTML. Line breaks in plain text are kept.',
}) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  const restoreSel = useRef<{ start: number; end: number } | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    const r = restoreSel.current;
    if (!el || !r) return;
    const len = value.length;
    const start = Math.min(r.start, len);
    const end = Math.min(r.end, len);
    el.setSelectionRange(start, end);
    restoreSel.current = null;
  }, [value]);

  const applyWrap = (open: string, close: string, ev: React.MouseEvent) => {
    ev.preventDefault();
    const el = ref.current;
    if (!el) return;
    const s = el.selectionStart;
    const e = el.selectionEnd;
    const v0 = el.value;
    const sel = v0.slice(s, e);
    const next = v0.slice(0, s) + open + sel + close + v0.slice(e);
    restoreSel.current = { start: s + open.length, end: s + open.length + sel.length };
    onChange(next);
  };

  const insertBr = (ev: React.MouseEvent) => {
    ev.preventDefault();
    const el = ref.current;
    if (!el) return;
    const s = el.selectionStart;
    const v0 = el.value;
    const ins = '<br/>';
    const next = v0.slice(0, s) + ins + v0.slice(s);
    restoreSel.current = { start: s + ins.length, end: s + ins.length };
    onChange(next);
  };

  const btnClass =
    'inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 shadow-sm hover:bg-slate-50';

  return (
    <div className="overflow-hidden rounded-md border border-slate-300 bg-white shadow-sm ring-1 ring-slate-900/[0.04]">
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 bg-slate-50 px-2 py-1.5">
        <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Format</span>
        <button type="button" className={btnClass} onMouseDown={(e) => applyWrap('<strong>', '</strong>', e)} title="Bold">
          <Bold className="h-3 w-3" />
          Bold
        </button>
        <button type="button" className={btnClass} onMouseDown={(e) => applyWrap('<em>', '</em>', e)} title="Italic">
          <Italic className="h-3 w-3" />
          Italic
        </button>
        <button type="button" className={btnClass} onMouseDown={insertBr} title="Line break">
          <CornerDownLeft className="h-3 w-3" />
          Break
        </button>
      </div>
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        spellCheck
        className={`block w-full resize-y border-0 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0 ${
          monospace ? 'font-mono text-xs leading-relaxed' : 'leading-snug'
        }`}
      />
      <p className="border-t border-slate-100 bg-slate-50/90 px-2 py-1.5 text-[10px] leading-snug text-slate-500">{hint}</p>
    </div>
  );
};
